import {
  BoxType,
  ImageFrameRole,
  type CustomImageTemplateBox,
  type CustomTemplateBox,
} from '@lite-workspace/dto-fotoclass-templates';
import { CollageData, CustomTemplateRef } from '../interfaces/collage-data.interface';
import {
  DrawableData,
  DrawableImageData,
  DrawableTextData,
  isDrawableGroup,
  isDrawableImage,
  isDrawableText,
} from '../interfaces/drawable-data.interface';
import { Template } from '../interfaces/template.interface';
import type { PersistedCollageV1 } from '../normalizer/types';

/** Что обходчику нужно на самом деле: коллаж, группа или обёртка вокруг одиночного drawable — у всех троих один список. */
export interface DrawableHost {
  readonly drawableObjects?: readonly DrawableData[];
}

export function getDrawableImages(collage: CollageData): DrawableImageData[] {
  return (collage.drawableObjects ?? []).filter(isDrawableImage);
}

/**
 * Рекурсивно собирает все DrawableImage, включая вложенные в группы (виньетки), НА ЛЮБОЙ ГЛУБИНЕ:
 * дизайн-пак заворачивает рамку в структурную подгруппу, и по одноуровневому счёту кадр в такой рамке
 * не считался использованным — сборка отдавала его в пул свободных и ставила второй раз. Глубина та же,
 * что у клиентского локатора (`forEachBookBox` в album-editor): мерки «кадр стоит в книге» одна.
 */
export function getAllDrawableImages(collage: DrawableHost): DrawableImageData[] {
  const images: DrawableImageData[] = [];
  const walk = (drawables: readonly DrawableData[] | undefined): void => {
    for (const d of drawables ?? []) {
      if (isDrawableImage(d)) images.push(d);
      else if (isDrawableGroup(d)) walk(d.drawableObjects);
    }
  };

  walk(collage.drawableObjects);

  return images;
}

export function getDrawableTexts(collage: CollageData): DrawableTextData[] {
  return (collage.drawableObjects ?? []).filter(isDrawableText);
}

export function getCollageSide(collageIndex: number, totalCollages: number): 'full' | 'left' | 'right' {
  if (totalCollages === 1) return 'full';
  if (collageIndex === 1) return 'right';
  return 'left';
}

export function getCollageImageIds(collage: CollageData): string[] {
  return getDrawableImages(collage)
    .map(d => d.image?.id)
    .filter((id): id is string => id !== undefined);
}

/**
 * Extracts the template ID from `originalCustomTemplate`, which may be either:
 * - `Template` (legacy array format where `[0]` is the ID)
 * - `CustomTemplateRef` / `CustomTemplate` (object format where `.id` is the ID)
 */
export function getOriginalCustomTemplateId(template: Template | CustomTemplateRef | null | undefined): string | undefined {
  if (!template) return undefined;

  if (!Array.isArray(template)) {
    return template.id;
  }

  return typeof template[0] === 'string' ? template[0] : undefined;
}

// ─── v1-твины обхода кадров коллажа (custom-template-v1, PersistedCollageV1) ─────────────────────
// Пар здесь ДВЕ, и глубина у них разная — каждая повторяет глубину СВОЕЙ v0-соседки, а не общую:
// плоская (`getDrawableImages` / `getDrawableImagesBoxes`) верхним уровнем и не спускается в группу
// вовсе; рекурсивная (`getAllDrawableImages*`) ходит НА ЛЮБУЮ ГЛУБИНУ — обе её ветви разом, иначе
// мерки разъедутся.
// Две ветки в каждой паре — состояние перехода: v0-ветви выше держат их живые вызывающие на
// CollageData, и они уходят с последним из них.

/**
 * v1-твин {@link getDrawableImages}: тот же верхний уровень, без спуска в группу.
 *
 * Плоскость здесь — закон, а не упрощение: рекурсивный твин увидел бы кадр ВНУТРИ группы (ростерная
 * ячейка, виньетка) и строитель обложки поставил бы фото в чужой слот. Закреплено там, где твин
 * зовут, — `spreads-generators-boxes.spec.ts`, шаблон `groupFirst`.
 */
export function getDrawableImagesBoxes(collage: PersistedCollageV1): CustomImageTemplateBox[] {
  return (collage.boxes ?? []).filter((box): box is CustomImageTemplateBox => box.type === BoxType.IMAGE);
}

/** v1-твин {@link getAllDrawableImages}: та же полная глубина — иначе мерки ветвей разъедутся. */
export function getAllDrawableImagesBoxes(collage: PersistedCollageV1): CustomImageTemplateBox[] {
  const images: CustomImageTemplateBox[] = [];
  const walk = (boxes: readonly CustomTemplateBox[] | undefined): void => {
    for (const box of boxes ?? []) {
      // Группа без `boxes` типом запрещена, но приходит прямым PATCH: не падаем, как соседи.
      if (box.type === BoxType.GROUP) walk(box.boxes);
      else if (box.type === BoxType.IMAGE) images.push(box);
    }
  };

  walk(collage.boxes);

  return images;
}

/**
 * Разворот, задуманный однокадровым: ровно ОДНА фоторамка на всех коллажах, и она размечена ролью
 * (например, «Общее фото класса» — `spread_full` под слотом AUTO_LAYOUT). Порог «из двух страниц одним
 * фото разворот не собрать» его не касается — единственная мерка на все гейты сборки: ремонт, каскад
 * деградации, закон ориентации. Разворот с ролевой рамкой СРЕДИ прочих однокадровым не считается:
 * льгота, выданная по одной рамке, переживала бы её рез и уводила бы в печать полупустой разворот.
 */
export function soleRoleFrame(spread: { collages: readonly DrawableHost[] }): DrawableImageData | null {
  const frames = spread.collages.flatMap(collage => getAllDrawableImages(collage));
  return frames.length === 1 && frames[0].frameRole ? frames[0] : null;
}

/** v1-твин {@link soleRoleFrame}: та же мерка на коробках. */
export function soleRoleFrameBoxes(spread: { collages: readonly PersistedCollageV1[] }): CustomImageTemplateBox | null {
  const frames = spread.collages.flatMap(collage => getAllDrawableImagesBoxes(collage));
  return frames.length === 1 && frames[0].params.frameRole ? frames[0] : null;
}

/**
 * Общий разворот класса: единственная фоторамка разворота размечена «Общее фото» (`COMMON_PHOTO`).
 * Своего типа у общего разворота больше нет — ОДНА мерка на все проходы сборки, которым нужно его
 * изъятие (закон ориентации на копии эталона, каскад, синхра): кадр мастера едет в альбом ученика
 * дословно (AC5). Рамка «Общее фото» среди прочих рамок разворот общим не делает — там её кормит
 * лестница ролей наравне с соседями.
 */
export function isCommonPhotoSpread(spread: { collages: readonly DrawableHost[] }): boolean {
  return soleRoleFrame(spread)?.frameRole === ImageFrameRole.COMMON_PHOTO;
}

/** v1-твин {@link isCommonPhotoSpread}. */
export function isCommonPhotoSpreadBoxes(spread: { collages: readonly PersistedCollageV1[] }): boolean {
  return soleRoleFrameBoxes(spread)?.params.frameRole === ImageFrameRole.COMMON_PHOTO;
}
